import { Tab, Tabs } from '@mui/material';
import { useWidgetExpandCollapse } from '@ptek/widget-core';
import {
  ComponentType,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TAB_IDS } from '../constants/app';
import { AppContext } from '../context/AppContext';
import { useAutoSelectDocumentTab } from '../hooks/useAutoSelectDocumentTab';
import { ContestPanel } from '../features/Contest';
import { ContextPanel } from '../features/Context';
import { useRecordingStore } from '../stores';
import { FEATURE_FLAGS } from '../types/featureFlags';
import { ContextDataItem } from '../types/widget';
import {
  hasFeature,
  useAutoExportConfig,
  useNotesTabLabel,
} from '../utils/feature-util';

import { DocumentPanel } from '../features/Document';
import { RecorderControlBar } from '../features/Recorder/components';
import { TranscriptPanel } from '../features/Transcript';
import { appLayoutStyles as styles } from '../styles/appLayoutStyles';
import { FlexColumn, FlexColumnStretch, TabContainer } from './LayoutBoxes';

interface TabConfig {
  id: (typeof TAB_IDS)[keyof typeof TAB_IDS];
  label: string;
  component: React.ComponentType<Record<string, unknown>>;
  props?: Record<string, unknown>;
}

interface AppLayoutProps {
  isAuthenticated: boolean;
  requireAuth: boolean;
  handleLogout: () => void;
  activeTab?: string;
  handleTabChange?: (tab: string) => void;
}

export const AppLayout: ComponentType<AppLayoutProps> = ({
  isAuthenticated,
  requireAuth,
  handleLogout,
  activeTab: activeTabProp,
  handleTabChange: handleTabChangeProp,
}) => {
  const { sessionModel, contestStatus } = useContext(AppContext);
  const { expand, collapse, isExpanded } = useWidgetExpandCollapse();

  const isRecording = useRecordingStore((state) => state.isRecording);
  const isPaused = useRecordingStore((state) => state.isPaused);

  const showContextTab = hasFeature(FEATURE_FLAGS.DOCUMENT_CONTEXT);
  const autoExportConfig = useAutoExportConfig();
  const hideSoapNotesTab = autoExportConfig?.hideSoapNotesTab === true;
  const notesTabLabel = useNotesTabLabel();
  const collapseTranscriptDuringGeneration =
    autoExportConfig?.collapseTranscriptDuringGeneration === true;

  const [localActiveTab, setLocalActiveTab] = useState<string>(
    TAB_IDS.transcript
  );
  const userSwitchedTabRef = useRef(false);
  const showContestTab = contestStatus.tabVisible;

  const activeTab = activeTabProp ?? localActiveTab;

  const [lastGeneratedContext, setLastGeneratedContext] = useState<
    ContextDataItem[] | null
  >(null);

  useEffect(() => {
    if (sessionModel.documents.length > 0 && !lastGeneratedContext) {
      setLastGeneratedContext(
        JSON.parse(JSON.stringify(sessionModel.contextData))
      );
    }
  }, [
    sessionModel.documents.length,
    sessionModel.contextData,
    lastGeneratedContext,
  ]);

  useAutoSelectDocumentTab({
    enabled: activeTabProp === undefined,
    documentsLength: sessionModel.documents.length,
    hideSoapNotesTab,
    userSwitchedTabRef,
    setActiveTab: setLocalActiveTab,
  });

  useEffect(() => {
    if (collapseTranscriptDuringGeneration && sessionModel.isGenerating) {
      collapse();
    }
  }, [collapseTranscriptDuringGeneration, sessionModel.isGenerating, collapse]);

  const localHandleTabChange = useCallback(
    (newTabId: string) => {
      userSwitchedTabRef.current = true;
      if (!isExpanded) {
        expand();
      }
      setLocalActiveTab(newTabId);
    },
    [isExpanded, expand]
  );
  const handleTabChange = handleTabChangeProp ?? localHandleTabChange;

  const availableTabs = useMemo((): TabConfig[] => {
    const tabs: TabConfig[] = [
      {
        id: TAB_IDS.context,
        label: 'Context',
        component: ContextPanel as React.ComponentType<Record<string, unknown>>,
        props: {
          handleTabChange,
          lastGeneratedContext,
          onContextGenerated: (contextData: ContextDataItem[]) => {
            setLastGeneratedContext(JSON.parse(JSON.stringify(contextData)));
          },
        },
      },
      {
        id: TAB_IDS.transcript,
        label: 'Transcript',
        component: TranscriptPanel as React.ComponentType<
          Record<string, unknown>
        >,
        props: { isRecording, isPaused, handleTabChange },
      },
      {
        id: TAB_IDS.document,
        label: notesTabLabel,
        component: DocumentPanel as React.ComponentType<
          Record<string, unknown>
        >,
      },
      {
        id: TAB_IDS.contest,
        label: 'Giveaway',
        component: ContestPanel as React.ComponentType<Record<string, unknown>>,
      },
    ];

    let filteredTabs = tabs;

    if (!showContextTab) {
      filteredTabs = filteredTabs.filter((tab) => tab.id !== TAB_IDS.context);
    }

    if (hideSoapNotesTab) {
      filteredTabs = filteredTabs.filter((tab) => tab.id !== TAB_IDS.document);
    }

    if (!showContestTab) {
      filteredTabs = filteredTabs.filter((tab) => tab.id !== TAB_IDS.contest);
    }

    return filteredTabs;
  }, [
    showContextTab,
    showContestTab,
    hideSoapNotesTab,
    notesTabLabel,
    isRecording,
    isPaused,
    handleTabChange,
    lastGeneratedContext,
  ]);

  const renderTabs = useCallback(() => {
    return availableTabs.map((tab) => (
      <Tab
        key={tab.id}
        label={tab.label}
        value={tab.id}
        onClick={() => {
          // MUI's onChange only fires when a *different* tab is selected, so
          // clicking the already-selected tab would do nothing.  Call expand()
          // here so that re-clicking the active tab still expands the widget.
          // The shell silently ignores expand() when already expanded.
          if (tab.id === activeTab) {
            expand();
          }
        }}
      />
    ));
  }, [availableTabs, activeTab, expand]);

  const renderTabContent = useCallback(() => {
    const activeTabConfig = availableTabs.find((tab) => tab.id === activeTab);
    if (!activeTabConfig) return null;

    const Component = activeTabConfig.component;
    return <Component {...activeTabConfig.props} />;
  }, [availableTabs, activeTab]);

  return (
    <FlexColumn sx={styles.rootContainer} data-widget-container>
      <FlexColumnStretch sx={styles.mainContent}>
        <RecorderControlBar
          onLogout={handleLogout}
          showLogout={requireAuth && isAuthenticated}
          handleTabChange={handleTabChange}
        />

        <TabContainer>
          <Tabs
            value={availableTabs.find((tab) => tab.id === activeTab)?.id}
            onChange={(_, newTab) => handleTabChange(newTab)}
            sx={{
              flex: 1,
              minHeight: '40px',
              maxHeight: '40px',
              '& .MuiTab-root': {
                minHeight: '40px',
              },
              '& .MuiTabs-indicator': {
                height: '3px',
              },
            }}
          >
            {renderTabs()}
          </Tabs>
        </TabContainer>

        <FlexColumnStretch sx={styles.tabContent}>
          {renderTabContent()}
        </FlexColumnStretch>
      </FlexColumnStretch>
    </FlexColumn>
  );
};
